/**
 * StatusBadge — small inline pill for HistoryCard.
 *
 * Maps a HistoryStatus enum (server) to a colored pill with emoji + label.
 * Y1 palette (per doc 06 §4.6):
 *  - printed (green):  bg #D1FAE5, text #15803D, ✓
 *  - shared_pdf:       bg #D1FAE5, text #15803D, ✓
 *  - shared_wa:        bg #D1FAE5, text #15803D, ✓
 *  - selesai_belum:    bg #F3F4F6, text #6B7280, ✓
 *  - belum_dicetak:    bg #F3F4F6, text #6B7280, ○
 */

import { StyleSheet, Text, View } from 'react-native';

import type { HistoryStatus } from '@/types/history';
import { STATUS_BADGE_LABEL } from '@/types/history';

interface StatusBadgeProps {
  status: HistoryStatus;
  /** Compact mode: no emoji, shorter padding. Default false. */
  compact?: boolean;
}

const STATUS_VISUAL: Record<
  HistoryStatus,
  { bg: string; text: string; emoji: string }
> = {
  printed: { bg: '#D1FAE5', text: '#15803D', emoji: '✓' },
  shared_pdf: { bg: '#D1FAE5', text: '#15803D', emoji: '✓' },
  shared_wa: { bg: '#D1FAE5', text: '#15803D', emoji: '✓' },
  selesai_belum_dicetak: { bg: '#F3F4F6', text: '#6B7280', emoji: '✓' },
  belum_dicetak: { bg: '#F3F4F6', text: '#6B7280', emoji: '○' },
};

export function StatusBadge({ status, compact = false }: StatusBadgeProps) {
  const visual = STATUS_VISUAL[status];
  const label = STATUS_BADGE_LABEL[status];
  return (
    <View
      style={[
        styles.pill,
        compact && styles.pillCompact,
        { backgroundColor: visual.bg },
      ]}
      accessibilityRole="text"
      accessibilityLabel={`Status: ${label}`}
    >
      <Text style={[styles.emoji, { color: visual.text }]}>{visual.emoji}</Text>
      <Text style={[styles.label, compact && styles.labelCompact, { color: visual.text }]}>
        {label}
      </Text>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 999,
    alignSelf: 'flex-start',
    gap: 4,
  },
  pillCompact: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    gap: 2,
  },
  emoji: {
    fontSize: 12,
    fontWeight: '700',
  },
  label: {
    fontSize: 12,
    fontWeight: '600',
  },
  labelCompact: {
    fontSize: 11,
  },
});
